import { useEffect, useState } from "react";
import { Zap, Bell, PartyPopper } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import ProductCard from "./ProductCard";

function useCountdown(target) {
  const [left, setLeft] = useState(() => Math.max(new Date(target) - Date.now(), 0));
  useEffect(() => {
    setLeft(Math.max(new Date(target) - Date.now(), 0));
    const t = setInterval(() => setLeft(Math.max(new Date(target) - Date.now(), 0)), 1000);
    return () => clearInterval(t);
  }, [target]);
  const s = Math.floor(left / 1000);
  return { hours: Math.floor(s / 3600), mins: Math.floor((s % 3600) / 60), secs: s % 60, done: left <= 0 };
}

const pad = (n) => String(n).padStart(2, "0");

function TimeBox({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <motion.span key={value} initial={{ y: -8, opacity: 0.4 }} animate={{ y: 0, opacity: 1 }} className="w-14 h-14 sm:w-16 sm:h-16 grid place-items-center rounded-2xl bg-[#1C1917] text-[#FDFBF7] font-serif text-2xl sm:text-3xl font-bold tabular-nums shadow-lg">
        {pad(value)}
      </motion.span>
      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500 mt-2">{label}</span>
    </div>
  );
}

export default function FlashSaleSection() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [reminded, setReminded] = useState({});

  useEffect(() => {
    api.get("/flash-sales").then(({ data }) => setData(data)).catch(() => setData({ active: [], upcoming: [] }));
  }, []);

  const sale = data?.active?.[0];
  const upcoming = data?.upcoming || [];
  const countdown = useCountdown(sale?.end || Date.now());

  if (!data) return null;

  const remind = async (saleId) => {
    if (!user) {
      toast.info("Sign in and we'll remind you when the sale goes live");
      nav("/auth");
      return;
    }
    try {
      const { data } = await api.post(`/flash-sales/${saleId}/remind`);
      setReminded((r) => ({ ...r, [saleId]: true }));
      toast.success(data.message);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 lg:py-20" data-testid="flash-sale-section">
      {sale && !countdown.done && (
        <>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-10">
            <div>
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#B45309]">
                <Zap size={14} fill="currentColor" /> Flash Sale
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 mt-3 tracking-tight">Hurry! Limited-Time Deals</h2>
              <p className="text-stone-500 mt-3 max-w-md">Grab your favourite gifts before the offer ends. Prices return to normal when the clock runs out.</p>
            </div>
            <div className="flex items-start gap-3" data-testid="flash-countdown">
              <TimeBox value={countdown.hours} label="Hours" />
              <span className="font-serif text-3xl text-stone-300 mt-3">:</span>
              <TimeBox value={countdown.mins} label="Minutes" />
              <span className="font-serif text-3xl text-stone-300 mt-3">:</span>
              <TimeBox value={countdown.secs} label="Seconds" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {sale.items.map((item) => (
              <ProductCard
                key={item.product_id}
                product={{
                  id: item.product.id, name: item.product.name, image: item.product.image,
                  price: item.product.price, original_price: item.product.original_price,
                  effective_price: item.flash_price, rating: item.product.rating, review_count: item.product.review_count,
                  stock: item.product.stock, delivery_types: [],
                  flash: { remaining: Math.min(item.remaining, item.product.stock), sold_qty: item.sold_qty, max_qty: item.max_qty },
                  discount_pct: Math.round((1 - item.flash_price / item.product.original_price) * 100),
                }}
              />
            ))}
          </div>
        </>
      )}

      {data && data.active.length === 0 && (
        <div className="text-center py-12 rounded-3xl bg-[#F9F3EA] border border-[#E9E2D7]" data-testid="flash-sale-ended">
          <PartyPopper className="mx-auto text-[#B45309]" size={32} />
          <h3 className="font-serif text-2xl font-semibold text-stone-900 mt-4">This Flash Sale has ended.</h3>
          <p className="text-stone-500 mt-2">Stay tuned for our next offer — it's always worth the wait.</p>
        </div>
      )}
      {sale && countdown.done && (
        <div className="text-center py-12 rounded-3xl bg-[#F9F3EA] border border-[#E9E2D7]" data-testid="flash-sale-expired">
          <PartyPopper className="mx-auto text-[#B45309]" size={32} />
          <h3 className="font-serif text-2xl font-semibold text-stone-900 mt-4">This Flash Sale has ended.</h3>
          <p className="text-stone-500 mt-2">Stay tuned for our next offer!</p>
        </div>
      )}

      {upcoming.length > 0 && (
        <div className="mt-10 rounded-3xl border-2 border-dashed border-[#E0A96D]/50 bg-[#FDFBF7] p-6 sm:p-8" data-testid="upcoming-sales">
          {upcoming.map((u) => (
            <div key={u.id} className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-1">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B45309]">Coming Soon</p>
                <h3 className="font-serif text-2xl font-semibold text-stone-900 mt-2">{u.name}</h3>
                <p className="text-sm text-stone-500 mt-1">
                  Starts {new Date(u.start).toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                </p>
                <div className="flex gap-2 mt-4">
                  {u.items.slice(0, 4).map((i) => (
                    <img key={i.product_id} src={i.product.image} alt={i.product.name} className="w-14 h-14 rounded-xl object-cover border border-stone-200" />
                  ))}
                </div>
              </div>
              <button onClick={() => remind(u.id)} disabled={reminded[u.id]} className="shrink-0 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#1C1917] text-[#FDFBF7] text-sm font-semibold hover:bg-[#B45309] transition-colors disabled:opacity-60" data-testid={`remind-${u.id}`}>
                <Bell size={16} /> {reminded[u.id] ? "We'll remind you!" : "Notify Me"}
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
